import { useTranslation } from 'react-i18next';
import { useEffect, useState } from "react";

import { Modal, message, Button, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import DynamicTable from '@/common/components/DynamicTable/DynamicTable';
import { API_ENDPOINTS, QUERY_KEYS } from '../../api/apiEndpoints';
import type { ClaimRequestType } from '../../types/claimType';
import { CLAIM_STATUS } from '@/common/staticData/options';
import { post } from '@/common/services/httpClient';
import {
    SyncOutlined, // 🔁 change status icon (Awaiting Approval)
    CheckOutlined, // 🟢 NEW: tick icon for "Mark as Paid"
    CloseOutlined, // ❌ reject
    DollarOutlined,
    EditOutlined,
    UndoOutlined,
} from '@ant-design/icons';
import StatusTag from '../../../../common/components/Status';
import { ClaimStatus, ClaimStatusName } from '../../../../common/staticData/enums';
import { useQueryClient } from "@tanstack/react-query";
import { hasPermission } from '../../../../common/services/permissionService';
import { getPermissionKeyFromApi } from '../../../../common/components/fromatPermission';
import { useClaimSettings } from '../../../../common/hooks/useClaimSettings';
import { formatPaddedId } from '../../../../common/helpers/formatHelpers';
import AmountFormat from '../../../../common/components/AmountFormat';
import { useRouter } from "@tanstack/react-router";
import { useAuthStore } from '../../../auth/store/authStore';

interface ApiResponse<T = unknown> {
    isSuccess?: boolean;
    isError?: boolean;
    message?: string;
    title?: string;
    data?: T;
    error?: boolean;
}


const ClaimRequests = () => {
    const [undoPaidMap, setUndoPaidMap] = useState<Record<string, number>>({});
    const { data: claimSettings } = useClaimSettings();
    const undoLimitMs = (claimSettings?.undoPaidTimeLimitMinutes ?? 5) * 60 * 1000;
    const router = useRouter();
    const queryClient = useQueryClient();

    const toDate = new Date();
    const fromDate = new Date();
    fromDate.setDate(toDate.getDate() - 15);

    const userType = useAuthStore((state) => state.userType);
    const shouldShowCompanyFilter = userType !== 'policyholder';

    useEffect(() => {
        const timer = setInterval(() => {
            setUndoPaidMap((prev) => {
                const now = Date.now();
                const updated = Object.fromEntries(
                    Object.entries(prev).filter(([, time]) => now - time <= undoLimitMs)
                );
                return updated;
            });
        }, 60 * 1000); // check every minute

        return () => clearInterval(timer);
    }, [undoLimitMs]);

    const { t } = useTranslation();



    const filters: FilterConfig[] = [];

    if (shouldShowCompanyFilter) {
        filters.push({
            field: 'companyId',
            type: 'select',
            placeholder: t('Select company'),
            showSearch: true,
            dynamicOptions: {
                url: '/Lookup/GetDropdownData',
                method: 'POST',
                body: { dropdowns: ['company'] },
                transformData: (apiResponse: unknown) => {
                    const response = apiResponse as { company: { Name: string; Id: number }[] };
                    return response.company.map(item => ({ label: item.Name, value: item.Id }));
                },
            },
        });
    }

    filters.push(
        {
            field: 'medicalProviderId',
            type: 'select',
            placeholder: t('Select medical provider'),
            showSearch: true,
            dynamicOptions: {
                url: '/Lookup/GetDropdownData',
                method: 'POST',
                body: { dropdowns: ['medicalprovider'] },
                transformData: (apiResponse: unknown) => {
                    const response = apiResponse as { medicalprovider: { Name: string; Id: number }[] };
                    return response.medicalprovider.map((item) => ({
                        label: item.Name,
                        value: item.Id,
                    }));
                },
            },
        },
        {
            field: 'ReferenceOrCardNo',
            placeholder: t('Search by reference or card number'),
            type: 'input',
        },
        {
            field: 'fromDate',
            placeholder: t('From Date'),
            type: 'date',
            required: true,
            requiredMessage: 'From date is required',
            defaultValue: dayjs(fromDate).format('YYYY-MM-DD'),
        },
        {
            field: 'toDate',
            placeholder: t('To Date'),
            type: 'date',
            required: true,
            requiredMessage: 'To Date is required',
            defaultValue: dayjs(toDate).format('YYYY-MM-DD'),
        },
        {
            field: 'status',
            placeholder: t('Status'),
            type: 'select',
            options: CLAIM_STATUS,
            defaultValue: null,
        },
        {
            field: 'Print',
            type: 'export',
            exportOptions: {
                url: API_ENDPOINTS.CLAIM.EXPORT,
                fileName: 'Claim',
                fileType: 'excel',
                defaultPayload: {},
                columns: [
                    { header: t('Card Number'), key: 'cardNumber' },
                    { header: t('Ref. #'), key: 'referenceNo' },
                    { header: t('Name'), key: 'policyHolderName' },
                    { header: t('Claim Date'), key: 'claimDate' },
                    { header: t('Company Name'), key: 'companyName' },
                    { header: t('Policy Code'), key: 'policyCode' },
                    { header: t('Medical Provider'), key: 'medicalProviderName' },
                    { header: t('Currency'), key: 'currencyISOCode' },
                    { header: t('Total Amount'), key: 'totalAmount' },
                    { header: t('Status'), key: 'status' },
                ],
            },
        },
    );

    // ✅ Handle status change to Awaiting Approval
    const handleChangeStatusToAwaiting = (record: ClaimRequestType) => {
        if (record.status !== ClaimStatus.Created) {
            message.warning(t('Only claims with status "Created" can be updated to Awaiting Approval.'));
            return;
        }

        Modal.confirm({
            title: t('Change Claim Status'),
            content: t('Are you sure you want to change the status of claim to Awaiting Approval?'),
            okText: t('Yes'),
            cancelText: t('No'),
            async onOk() {
                try {
                    const payload = { id: record.id };
                    const response = await post<ApiResponse>(API_ENDPOINTS.CLAIM.AWAITINGAPPROVAL_STATUS, payload);

                    if (!response?.isError) {
                        message.success(t('Claim status updated to Awaiting Approval successfully'));

                        // Invalidate claim list so table refreshes
                        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.CLAIM.SEARCH });
                    } else {
                        message.error(response.message);
                    }
                } catch {
                    message.error(t('An error occurred while updating the status'));
                }
            },
        });
    };

    // 🟢 Handle status change from Approved → Paid
    const handleMarkAsPaid = (record: ClaimRequestType) => {
        if (record.status !== ClaimStatus.Approved) {
            message.warning(t('Only claims with status "Approved" can be marked as Paid.'));
            return;
        }

        Modal.confirm({
            title: t('Mark Claim as Paid'),
            content: t('Are you sure you want to mark claim as Paid?'),
            okText: t('Yes'),
            cancelText: t('No'),
            async onOk() {
                try {
                    const payload = { id: record.id, newStatus: 'Paid' };
                    const response = await post<ApiResponse>(API_ENDPOINTS.CLAIM.PAID_STATUS, payload); // 🟢 Ensure endpoint exists

                    if (!response?.isError) {
                        message.success(t('Claim status updated to Paid successfully'));

                        // Store timestamp for Undo eligibility
                        setUndoPaidMap((prev) => ({
                            ...prev,
                            [record.id]: Date.now(),
                        }));

                        // Refresh the claim list
                        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.CLAIM.SEARCH });
                    } else {
                        message.error(response.message);
                    }
                } catch {
                    message.error(t('An error occurred while updating the status'));
                }
            },
        });
    };

    const handleUndoPaid = async (record: ClaimRequestType) => {
        try {
            const response = await post<ApiResponse>(API_ENDPOINTS.CLAIM.UNDO_PAID_STATUS, { id: record.id });

            if (!response?.isError) {
                message.success(t("Payment reverted successfully."));

                // Remove from undo list
                setUndoPaidMap((prev) => {
                    const newMap = { ...prev };
                    delete newMap[record.id];
                    return newMap;
                });

                queryClient.invalidateQueries({ queryKey: QUERY_KEYS.CLAIM.SEARCH });
            } else {
                message.error(response.message);
            }
        } catch {
            message.error(t("Failed to undo paid status."));
        }
    };

    const Columns: ColumnsType<ClaimRequestType> = [
        {
            title: t('Claim #'),
            dataIndex: 'claimNo',
            key: 'claimNo',
            render: (_, record) => (
                <Button
                    type="link"
                    onClick={() => {
                        router.navigate({
                            to: '/claim/requests/detail', // Corrected path
                            state: { record: record } as Record<string, unknown>,
                        });
                    }}
                >
                    {formatPaddedId(Number(record.id))}
                </Button>
            )
        },
        {
            title: t('Reference No'),
            dataIndex: 'referenceNo',
            key: 'referenceNo',
            fixed: 'left',
        },
        { title: t('Card Number'), dataIndex: 'cardNumber', key: 'cardNumber' },
        { title: t('Patient Name'), dataIndex: 'patientName', key: 'patientName' },
        {
            title: t('Claim Date'),
            dataIndex: 'claimDate',
            key: 'claimDate',
            render: (text) => (text ? dayjs(text).format('DD-MM-YYYY') : ''),
        },
        { title: t('Policy Code'), dataIndex: 'policyCode', key: 'policyCode' },
        { title: t('Plan Name'), dataIndex: 'planName', key: 'planName' },
        { title: t('Medical Provider'), dataIndex: 'medicalProviderName', key: 'medicalProviderName' },
        { title: t('Consultant'), dataIndex: 'consultantName', key: 'consultantName' },
        {
            title: t('Visit Date'),
            dataIndex: 'visitDate',
            key: 'visitDate',
            render: (text) => (text ? dayjs(text).format('DD-MM-YYYY') : ''),
        },
        {
            title: t('Total Amount'), dataIndex: 'totalAmount', key: 'totalAmount',
            align: "right",
            render: (value: number, record: ClaimRequestType) => (
                <AmountFormat
                    value={value ?? 0}
                    currencyCode={record.currencyISOCode ?? ""}
                    amountInWord={false}
                />
            )
        },
        // { title: t('Currency'), dataIndex: 'currencyISOCode', key: 'currencyISOCode' },
        {
            title: t('Status'),
            dataIndex: 'status',
            key: 'status',
            fixed: 'right',
            render: (status: string) => (
                <StatusTag status={ClaimStatusName[ClaimStatus[status as keyof typeof ClaimStatus]]} />
            ),
        },
    ];

    const awaitingApprovalPermission = hasPermission(getPermissionKeyFromApi(API_ENDPOINTS.CLAIM.AWAITINGAPPROVAL_STATUS));
    const paidPermission = hasPermission(getPermissionKeyFromApi(API_ENDPOINTS.CLAIM.PAID_STATUS));
    const approvePermission = hasPermission(getPermissionKeyFromApi(API_ENDPOINTS.CLAIM.APPROVED_STATUS));
    const rejectPermission = hasPermission(getPermissionKeyFromApi(API_ENDPOINTS.CLAIM.REJECTED_STATUS));

    const myActions = [
        {
            render: (record: ClaimRequestType) => {

                const status = record.status as ClaimStatus;
                const policyHolderStatus = record.policyHolderStatus;

                //console.log('policyHolderStatus', policyHolderStatus);

                const eligibleForApproveReject = [
                    ClaimStatus.AwaitingApproval,
                    ClaimStatus.Approved,
                    ClaimStatus.Rejected,
                ] as ClaimStatus[];

                if (policyHolderStatus == "Active" && status === ClaimStatus.Created && awaitingApprovalPermission) {
                    return (
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <Tooltip title={t("Awaiting Approval")}>
                                <Button
                                    shape="circle"
                                    icon={<SyncOutlined />}
                                    onClick={() => handleChangeStatusToAwaiting(record)}
                                />
                            </Tooltip>
                        </div>
                    );
                }

                // If approved -> can show "Mark as Paid", "Update Approval", or "Reject"
                if (policyHolderStatus == "Active" && status === ClaimStatus.Approved) {
                    const actionButtons = [];

                    if (paidPermission) {
                        actionButtons.push(
                            <Tooltip key="paid" title={t("Mark as Paid")}>
                                <Button
                                    shape="circle"
                                    icon={<DollarOutlined style={{ color: "goldenrod" }} />}
                                    onClick={() => handleMarkAsPaid(record)}
                                />
                            </Tooltip>
                        );
                    }

                    if (approvePermission) {
                        actionButtons.push(
                            <Tooltip key="update" title={t("Update Approval")}>
                                <Button
                                    shape="circle"
                                    icon={<EditOutlined style={{ color: "green" }} />}
                                    onClick={() => router.navigate({ to: '/claim/requests/approve-reject', state: { claimId: record.id, PolicyHolderId: record.policyHolderId, type: 'approve' } as Record<string, unknown> })}
                                />
                            </Tooltip>
                        );
                    }

                    if (rejectPermission) {
                        actionButtons.push(
                            <Tooltip key="reject" title={t("Reject Claim")}>
                                <Button
                                    shape="circle"
                                    icon={<CloseOutlined style={{ color: "red" }} />}
                                    onClick={() => router.navigate({ to: '/claim/requests/approve-reject', state: { claimId: record.id, PolicyHolderId: record.policyHolderId, type: 'reject' } as Record<string, unknown> })}
                                />
                            </Tooltip>
                        );
                    }

                    if (actionButtons.length > 0) {
                        return (
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                {actionButtons}
                            </div>
                        );
                    }
                }

                // For AwaitingApproval or Rejected also show Approve / Reject
                if (policyHolderStatus == "Active" && eligibleForApproveReject.includes(status) && status != ClaimStatus.Rejected) {
                    const actionButtons = [];

                    if (approvePermission) {
                        actionButtons.push(
                            <Tooltip key="approve" title={t("Approve Claim")}>
                                <Button
                                    shape="circle"
                                    icon={<CheckOutlined style={{ color: "green" }} />}
                                    onClick={() => router.navigate({ to: '/claim/requests/approve-reject', state: { claimId: record.id, PolicyHolderId: record.policyHolderId, type: 'approve' } as Record<string, unknown> })}
                                />
                            </Tooltip>
                        );
                    }

                    if (rejectPermission) {
                        actionButtons.push(
                            <Tooltip key="reject" title={t("Reject Claim")}>
                                <Button
                                    shape="circle"
                                    icon={<CloseOutlined style={{ color: "red" }} />}
                                    onClick={() => router.navigate({ to: '/claim/requests/approve-reject', state: { claimId: record.id, PolicyHolderId: record.policyHolderId, type: 'reject' } as Record<string, unknown> })}
                                />
                            </Tooltip>
                        );
                    }

                    if (actionButtons.length > 0) {
                        return (
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                {actionButtons}
                            </div>
                        );
                    }
                }

                if (policyHolderStatus == "Active" && status === ClaimStatus.Paid && paidPermission) {
                    const paidTime = undoPaidMap[record.id];
                    const canUndo = paidTime && Date.now() - paidTime <= undoLimitMs
                    return (
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            {canUndo && (
                                <Tooltip title={t("Undo Paid")}>
                                    <Button
                                        shape="circle"
                                        icon={<UndoOutlined style={{ color: "red" }} />}
                                        onClick={() => handleUndoPaid(record)}
                                    />
                                </Tooltip>
                            )}
                        </div>
                    );
                }

                return null;
            }
        },
    ];


    return (
        <div>
            <DynamicTable<ClaimRequestType>
                dataApiUrl={API_ENDPOINTS.CLAIM.SEARCH}
                baseQueryKey={QUERY_KEYS.CLAIM.SEARCH}
                columns={Columns}
                tableTitle={t('Claim Requests')}
                showActions={{
                    edit: (record) => record.status == "Created",
                    delete: (record) => record.status == "Created",
                }}
                actionColumnWidth={170}
                deleteBaseUrl={API_ENDPOINTS.CLAIM.DELETE}
                deletePayloadField="id" 
                customActions={myActions}
                filterConfigs={filters}
            />
        </div>
    );

};

export default ClaimRequests;
